import {action, computed, makeObservable, observable, runInAction} from "mobx";

import {AdminApi} from "../../../api/api";
import {HttpError} from "../../../api/http-error";
import {UserAddInput} from "../../../api/values/user-add-input";
import {UserInput} from "../../../api/values/user-input";
import {UserRole} from "../../../api/values/user-role-type";
import {UserSearchInput} from "../../../api/values/user-search-input";
import {UserUpdateInput} from "../../../api/values/user-update-input";
import {Stores} from "../../../stores";
import {Permission} from "../../../stores/authentication-store";
import {SimpleToast} from "../../../stores/toast-store";
import {BigIntId} from "../../../utils/big-int-id";
import {conflictChecker} from "../../../utils/conflict-checker-util";
import {PageStoreConstructorOptions} from "../../../utils/page-store";
import {PageRouter} from "../../../utils/route-util";
import {emailValidator} from "../../../utils/validation/rules/email-validator";
import {usernameValidator} from "../../../utils/validation/rules/username-validator";
import {ValidationResult, ValidationResultEmpty} from "../../../utils/validation/validation-result";
import {withInProgress} from "../../../utils/with-in-progress";

export interface User {
    name: string;
    email: string;
}

export class UserEditPageStore {
    @observable
    public isNew = true;

    @observable
    public id?: BigIntId = undefined;

    @observable
    public mediaId: string = "";

    @observable
    public name: string = "";

    @observable
    public editingName: string = "";

    @observable
    public email: string = "";

    @observable
    public role: UserRole = UserRole.ADMIN;

    @observable // adminURLは後ほど確認、設定ページのpathでいいはず？
    public adminURL: string = process.env.ADMIN_URL || location.origin + "/admin/password-setting";

    @observable
    public enabled: boolean = false;

    @observable
    public nameValidationResult: ValidationResult = ValidationResultEmpty;

    @observable
    public emailValidationResult: ValidationResult = ValidationResultEmpty;

    @observable
    public isNameValidationInProgress: boolean = false;

    @observable
    public isNameConflicted: boolean = false;

    @observable
    public isSavingInProgress: boolean = false;

    @observable
    public userList: User[] = [];

    @observable
    public isLoadingUserList: boolean = false;

    @observable
    public isExistingUser: boolean = false;
    @observable
    public originalName: string = "";

    @observable
    public existingUserFlag: boolean = false;

    @observable
    public isSuccessSaved: boolean = false;

    private adminApi: AdminApi;
    private stores: Stores;
    private pageRouter: PageRouter;

    constructor(options: PageStoreConstructorOptions) {
        this.adminApi = options.adminApi;
        this.stores = options.stores;
        this.pageRouter = options.pageRouter;

        makeObservable(this);
    }

    @observable
    public userSuggestions: User[] = [];
    @observable
    public previousEmail: string = "";
    @observable
    public isDeletingInProgress: boolean = false;
    @computed
    public get canSave() {
        return (
            this.isExistingUser ||
            (this.editingName.length > 0 &&
                this.email.length > 0 &&
                this.nameValidationResult.isEmpty &&
                this.emailValidationResult.isEmpty &&
                !this.isNameValidationInProgress &&
                !this.isNameConflicted &&
                !this.isSavingInProgress)
        );
    }

    @computed
    public get canEdit() {
        return (
            this.stores.authenticationStore.hasManageAdminUsersPermission ||
            !this.role.equals(UserRole.ADMIN)
        );
    }

    @computed
    public get availableRoles() {
        if (!this.canEdit) {
            return [this.role];
        }
        if (!this.stores.authenticationStore.hasManageAdminUsersPermission) {
            return UserRole.values.filter((role) => !role.equals(UserRole.ADMIN));
        } else {
            return UserRole.values;
        }
    }

    private checkConflictName = conflictChecker(
        (b) => (this.isNameValidationInProgress = b),
        (b) => (this.isNameConflicted = b),
        async (currentValue) => {
            const input = new UserSearchInput({
                mediaId: this.mediaId,
                name: currentValue,
            });
            const result = await this.adminApi.getUsers(input);
            return result.users.length > 0;
        },
    );

    public async initialize({query}: any) {
        this.stores.authenticationStore.checkAnyPermissionExists(
            Permission.MANAGE_ADMIN_USERS,
            Permission.MANAGE_USERS,
        );

        const id = query && query.id;
        if (!id) {
            throw new Error("id is not set");
        }

        this.setMediaId(query.mediaId || "");

        if (id === "new") {
            await this.clear();
        } else {
            await this.load(new BigIntId(id));
        }
    }

    @action.bound
    public async setIsSuccessSaved() {
        this.isSuccessSaved = true;
    }

    @action.bound
    public async setMediaId(s: string) {
        this.mediaId = s;
    }

    @action.bound
    public async setName(s: string) {
        this.editingName = s;
        (await usernameValidator(s)).run((v) => (this.nameValidationResult = v));

        if (this.existingUserFlag && this.editingName !== this.originalName) {
            runInAction(() => {
                this.isExistingUser = false;
                this.existingUserFlag = false;
                this.email = this.previousEmail;
            });
        }

        if (this.nameValidationResult.isEmpty) {
            this.validateName();
        }
    }

    @action.bound
    public async selectUserFromSuggestions(user: User) {
        this.setExistingUser(user);
    }

    @action.bound
    public async setExistingUser(user: User | null) {
        if (user) {
            this.isExistingUser = true;
            this.previousEmail = this.email;
            this.editingName = user.name;
            this.email = user.email;
            this.originalName = user.name;
            this.existingUserFlag = true;
        } else {
            this.isExistingUser = true;
            this.editingName = "";
            this.email = "";
            this.originalName = "";
            this.existingUserFlag = false;
        }
    }

    @action.bound
    public async setEmail(s: string) {
        this.email = s;
        (await emailValidator(s)).run((v) => (this.emailValidationResult = v));
    }

    @action.bound
    public async setRole(r: UserRole) {
        this.role = r;
    }

    @action.bound
    public async setEnabled(b: boolean) {
        this.enabled = b;
    }

    @action.bound
    public async clear() {
        this.isNew = true;
        this.id = undefined;
        this.name = "";
        this.editingName = "";
        this.email = "";
        this.role = UserRole.WRITER;
        this.enabled = true;
        this.isExistingUser = false;
    }

    @action.bound
    public async load(id: BigIntId) {
        const result = await this.adminApi.getUser(
            {id},
            new UserInput({
                mediaId: this.mediaId,
            }),
        );

        runInAction(() => {
            this.isNew = false;
            this.id = id;
            this.name = result.name;
            this.editingName = result.name;
            this.email = result.email;
            this.role = UserRole.fromName(result.role) || UserRole.WRITER;
            this.enabled = result.enabled;
            this.isExistingUser = false;
        });
    }

    @action.bound
    public async save() {
        await withInProgress(
            (b) => (this.isSavingInProgress = b),
            async () => {
                if (this.isNew) {
                    await this.saveNew();
                } else {
                    await this.saveToExisting();
                }
                if (this.isSuccessSaved) {
                    this.stores.toastStore.setSimpleToast(SimpleToast.SAVED);
                    this.stores.toastStore.keepFirstRouteChange();

                    this.pageRouter.pushRoute("/admin/[mediaId]/users", {
                        mediaId: this.mediaId,
                        enabled: "true",
                    });
                }
            },
        );
    }

    @action.bound
    public validateName() {
        this.checkConflictName(this.name, this.editingName);
    }

    @action.bound
    private async saveNew() {
        try {
            const userInput = new UserAddInput({
                email: this.email,
                name: this.editingName,
                role: this.role.name,
                existingUserFlag: this.existingUserFlag,
                adminURL: this.adminURL,
            });

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const response = await this.adminApi.postUser(
                {mediaId: this.mediaId, adminURL: this.adminURL},
                userInput,
            );
            if (response) {
                this.setIsSuccessSaved();
            }
        } catch (error: any) {
            console.error("API Error:", error.response ? error.response.data : error);
        }
    }

    @action.bound
    private async saveToExisting() {
        try {
            await this.adminApi.patchUser(
                {mediaId: this.mediaId},
                {id: this.id!},
                new UserUpdateInput({
                    email: this.email,
                    enabled: this.enabled,
                    name: this.editingName,
                    role: this.role.name,
                }),
            );
            this.setIsSuccessSaved();
        } catch (error) {
            if (error instanceof HttpError) {
                console.error(error.message);
            }
        }
    }

    @action.bound
    public async fetchUserList() {
        this.isLoadingUserList = true;
        try {
            const result = await this.adminApi.getTenantUsers(
                new UserSearchInput({
                    mediaId: this.mediaId,
                }),
            );
            runInAction(() => {
                this.userList = result.users.map((user) => ({
                    name: user.name,
                    email: user.email,
                }));
            });
        } catch (error) {
            console.error("ユーザー一覧の取得に失敗しました", error);
        } finally {
            runInAction(() => {
                this.isLoadingUserList = false;
            });
        }
    }

    @action.bound
    async handleDeleteUser() {
        this.isDeletingInProgress = true;
        try {
            await this.adminApi.deleteUser({mediaId: this.mediaId}, {id: this.id!});
            this.stores.toastStore.setSimpleToast(SimpleToast.DELETED);
            this.pageRouter.pushRoute("/admin/[mediaId]/users", {
                mediaId: this.mediaId,
                enabled: "true",
            });
        } catch (error) {
            console.error("削除エラー:", error);
            this.stores.toastStore.setSimpleToast(SimpleToast.USER_DELETE_FAILED);
        } finally {
            runInAction(() => {
                this.isDeletingInProgress = false;
            });
        }
    }
}
